import moment from 'moment';
import { useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Text } from 'rizzui';
import useFetch from '../../../../../hooks/useFetch';
import { menuRoutes } from '../../../../../router/menu';
import { CallsService } from '../../../../../services/calls/callsService';
import { Call, CallsApiResponse } from '../../../../../type/entities/calls-type';
import ContractProfileLayoutWrapper from '../../ContractProfileLayoutWrapper';
import { InfoCard } from '../info/components/InfoDisplay';

const formatDurationHuman = (seconds?: number | null): string => {
    if (!seconds && seconds !== 0) return 'N/A';

    const totalSeconds = Math.round(seconds);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;

    const parts = [];
    if (h > 0) parts.push(`${h} h`);
    if (m > 0) parts.push(`${m} min`);
    if (s > 0 || parts.length === 0) parts.push(`${s} s`);

    return parts.join(' ');
};

const formatCallDate = (value?: string | null): string => {
    if (!value) return 'N/A';

    const date = moment(value);
    return date.isValid() ? date.format('DD/MM/YYYY HH:mm') : 'N/A';
};

const ContractCalls = () => {
    const { id = '' } = useParams<{ id: string }>();
    const service = new CallsService();

    const [data, loading] = useFetch(useCallback(async () => {
        if (!id) return null;

        const response = await service.listCalls({
            page: 1,
            limit: 50,
            filter_filters: {
                contract: id,
            },
            filter_order: [
                {
                    field: 'callDate',
                    order: 'desc',
                }
            ],
        });

        return response.getResponseData() as CallsApiResponse;
    }, [id]));

    const relatedCalls = (data?.data ?? []) as Call[];

    return (
        <ContractProfileLayoutWrapper isLoading={loading}>
            <div className="pt-4">
                <InfoCard
                    title="Llamadas relacionadas"
                    subtitle="Llamadas realizadas desde esta ficha de contrato y vinculadas automáticamente al contrato."
                    collapsible={false}
                >
                    {loading ? (
                        <Text className="text-sm text-gray-500">Cargando llamadas relacionadas...</Text>
                    ) : relatedCalls.length === 0 ? (
                        <Text className="text-sm text-gray-500">No hay llamadas relacionadas con este contrato.</Text>
                    ) : (
                        <div className="space-y-3">
                            {relatedCalls.map((call) => (
                                <div key={call.id} className="rounded-md border border-gray-200 bg-white p-4">
                                    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                                        <div className="grid flex-1 grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                                            <div>
                                                <p className="text-xs font-medium text-gray-500">Fecha</p>
                                                <p className="text-sm font-semibold text-gray-900">{formatCallDate(call.callDate)}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs font-medium text-gray-500">Agente</p>
                                                <p className="text-sm font-semibold text-gray-900">
                                                    {[call.userName, call.userLastName].filter(Boolean).join(' ') || 'N/A'}
                                                </p>
                                                {call.userEmail && (
                                                    <p className="text-xs text-gray-500">{call.userEmail}</p>
                                                )}
                                            </div>
                                            <div>
                                                <p className="text-xs font-medium text-gray-500">Resultado</p>
                                                <p className="text-sm font-semibold text-gray-900">{call.classificationName || call.status || 'N/A'}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs font-medium text-gray-500">End text OCM</p>
                                                <p className="text-sm font-semibold text-gray-900">{call.ocmEndText || 'Sin datos'}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs font-medium text-gray-500">Duración</p>
                                                <p className="text-sm font-semibold text-gray-900">{formatDurationHuman(call.duration)}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs font-medium text-gray-500">Cuenta para incidencia</p>
                                                <p className="text-sm font-semibold text-gray-900">{call.countsAsFailedVerificationCall ? 'Sí' : 'No'}</p>
                                            </div>
                                            <div className="md:col-span-2 xl:col-span-4">
                                                <p className="text-xs font-medium text-gray-500">Comentario</p>
                                                <p className="text-sm text-gray-900 whitespace-pre-wrap">{call.comment || 'N/A'}</p>
                                            </div>
                                        </div>

                                        <div className="shrink-0">
                                            <Link
                                                to={`${menuRoutes.calls.path}/${call.id}/profile${menuRoutes.calls.profile.info}`}
                                                state={{
                                                    fromContract: true,
                                                    returnToContractPath: `${menuRoutes.contracts.path}/${id}/profile${menuRoutes.contracts.profile.calls}`,
                                                }}
                                                className="text-sm font-semibold text-primary"
                                            >
                                                Ver llamada
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </InfoCard>
            </div>
        </ContractProfileLayoutWrapper>
    );
};

export default ContractCalls;
